/**
 * Footer Component
 * Airtel-inspired footer with links and information
 */

import React from 'react';
import { Box, Container, Grid, Typography, Link, Divider } from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import BRANDING from '../shared/config/branding';

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        backgroundColor: '#f5f5f7',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {BRANDING.company.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enterprise IoT device management and analytics platform.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Link 
                href={`mailto:${BRANDING.company.email}`} 
                color="text.secondary" 
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}
              >
                <EmailIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption">{BRANDING.company.email}</Typography>
              </Link>
              <Link 
                href={BRANDING.company.website} 
                target="_blank" 
                rel="noopener noreferrer"
                color="text.secondary" 
                sx={{ textDecoration: 'none', fontSize: '0.875rem' }}
              >
                Visit positivezero.com
              </Link>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {BRANDING.footer.quickLinks.map((link, index) => (
                <Link
                  key={index}
                  href={link.path}
                  target={link.path.startsWith('http') ? '_blank' : undefined}
                  rel={link.path.startsWith('http') ? 'noopener noreferrer' : undefined}
                  color="text.secondary"
                  sx={{ textDecoration: 'none', fontSize: '0.875rem' }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Support */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {BRANDING.footer.support.map((link, index) => (
                <Link
                  key={index}
                  href={link.path}
                  target={link.path.startsWith('http') ? '_blank' : undefined}
                  rel={link.path.startsWith('http') ? 'noopener noreferrer' : undefined}
                  color="text.secondary"
                  sx={{ textDecoration: 'none', fontSize: '0.875rem' }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {BRANDING.footer.legal.map((link, index) => (
                <Link
                  key={index}
                  href={link.path}
                  target={link.path.startsWith('http') ? '_blank' : undefined}
                  rel={link.path.startsWith('http') ? 'noopener noreferrer' : undefined}
                  color="text.secondary"
                  sx={{ textDecoration: 'none', fontSize: '0.875rem' }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Copyright */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()} {BRANDING.company.fullName}. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link
              href={BRANDING.company.website}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                Powered by positivezero.com
              </Typography>
            </Link>
            <Typography variant="caption" color="text.secondary">
              Version 1.0.0
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;

